import { useEffect, useRef, useState } from "react";
import { ImagePlus, Search, Upload, X } from "lucide-react";
import { listMedia } from "@/lib/api/media.functions";
import { mediaUploadResultMessage, uploadMediaFiles } from "@/lib/media/upload";
import type { MediaSelection } from "@/lib/media/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type MediaRow = Awaited<ReturnType<typeof listMedia>>[number];

type MediaPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value?: MediaSelection | null;
  onSelect: (selection: MediaSelection) => void;
};

export function MediaPicker({ open, onOpenChange, value, onSelect }: MediaPickerProps) {
  const [files, setFiles] = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listMedia()
      .then(setFiles)
      .catch(() => toast.error("Failed to load media"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = files.filter(
    (f) =>
      !search ||
      f.filename.toLowerCase().includes(search.toLowerCase()) ||
      (f.altText ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  async function handleUpload(fileList: FileList | null) {
    const selected = fileList ? Array.from(fileList) : [];
    if (selected.length === 0) return;

    const result = await uploadMediaFiles(selected);
    if (result.uploaded.length > 0) {
      setFiles((prev) => [...result.uploaded, ...prev]);
      if (result.uploaded.length === 1 && result.failed.length === 0) {
        const row = result.uploaded[0];
        onSelect({ mediaId: row.id, url: row.path, altText: row.altText });
        onOpenChange(false);
        toast.success("Uploaded and selected");
        return;
      }
      toast.success(mediaUploadResultMessage(result));
    } else {
      toast.error(mediaUploadResultMessage(result));
    }
    if (result.failed.length > 0 && result.uploaded.length > 0) {
      toast.error(
        result.failed.map((f) => `${f.filename}: ${f.error}`).join("; "),
        { duration: 8000 },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select from media library</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search files…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              void handleUpload(e.target.files);
              e.target.value = "";
            }}
          />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No images found.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {filtered.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => {
                    onSelect({ mediaId: file.id, url: file.path, altText: file.altText });
                    onOpenChange(false);
                  }}
                  className={cn(
                    "overflow-hidden rounded-md border text-left transition hover:ring-2 hover:ring-primary",
                    value?.mediaId === file.id && "ring-2 ring-primary",
                  )}
                >
                  <img
                    src={file.path}
                    alt={file.altText ?? file.filename}
                    className="aspect-square w-full object-cover"
                  />
                  <p className="truncate p-1.5 text-xs">{file.filename}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type MediaFieldProps = {
  label: string;
  value: MediaSelection;
  onChange: (value: MediaSelection) => void;
  className?: string;
};

type MediaThumbnailProps = {
  value: MediaSelection;
  onOpen: () => void;
  onClear: () => void;
  size?: "sm" | "md";
};

export function MediaThumbnail({ value, onOpen, onClear, size = "md" }: MediaThumbnailProps) {
  const box = size === "sm" ? "h-16 w-16" : "h-24 w-24";
  const icon = size === "sm" ? "h-5 w-5" : "h-8 w-8";
  const clearBtn = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const clearIcon = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";

  return (
    <div className={cn("relative shrink-0", box)}>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "flex h-full w-full items-center justify-center overflow-hidden rounded-md border bg-muted/30 transition hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        )}
        aria-label={value.url ? "Change image" : "Select from library"}
      >
        {value.url ? (
          <img src={value.url} alt={value.altText ?? ""} className="h-full w-full object-cover" />
        ) : (
          <ImagePlus className={cn(icon, "text-muted-foreground")} />
        )}
      </button>
      {value.url ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className={cn(
            "absolute -right-1.5 -top-1.5 z-10 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm transition hover:bg-destructive/90",
            clearBtn,
          )}
          aria-label="Remove image"
        >
          <X className={clearIcon} />
        </button>
      ) : null}
    </div>
  );
}

export function MediaField({ label, value, onChange, className }: MediaFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("space-y-2", className)}>
      <span className="text-sm font-medium">{label}</span>
      <MediaThumbnail
        value={value}
        onOpen={() => setOpen(true)}
        onClear={() => onChange({ mediaId: null, url: null, altText: null })}
      />
      <MediaPicker open={open} onOpenChange={setOpen} value={value} onSelect={onChange} />
    </div>
  );
}
